/**
 * Thin adapters over `@viamericas/auth-react` (Concern 1 — Entra SSO).
 *
 * This module is the *only* place in the SPA that imports the
 * `@viamericas/auth-react` package directly. Every other module talks to the
 * interfaces the SPA already defines (`MsalConfig` in `./config`, `TokenClient`
 * in `./authedFetch`). Keeping the package surface behind these adapters means
 * a future package change stays localized here instead of rippling across the
 * app.
 *
 * The adapters are deliberately thin: a config mapper, a `TokenClient` shaped
 * over `useAuth()`, two role/sign-out helpers, and re-exports of the provider
 * and route guard for the app wrapper (task 3.12) to consume.
 *
 * Requirements: 13.1 (package isolated behind adapters), 13.3 (config mapped
 * from the SPA's `MsalConfig`), 13.5 (token/role/sign-out behavior expressed
 * over the package's public hooks).
 */

import { useMemo, useRef } from "react";
import {
  EntraAuthProvider,
  RequireAuth,
  useAuth,
  useHasRole,
  type EntraAuthConfig,
} from "@viamericas/auth-react";

import type { MsalConfig } from "./config";
import type { TokenClient } from "./authedFetch";

/**
 * Entra App Role that gates dashboard data access (Req 5.x). Matched
 * case-sensitively by the package's role hooks.
 */
export const VIEWER_ROLE = "ViaMind.Viewer" as const;

/**
 * Map the SPA's resolved {@link MsalConfig} to the package's
 * {@link EntraAuthConfig} shape consumed by `<EntraAuthProvider config={...}>`.
 *
 * The field names line up one-to-one with the verified package API
 * (`tenantId` / `clientId` / `apiScope` / `redirectUri`), so this is a direct,
 * loss-free projection. `redirectUri` is always supplied because `readMsalConfig`
 * has already resolved it (explicit `VITE_REDIRECT_URI` or the current origin).
 *
 * @param config The SPA's resolved MSAL configuration.
 * @returns The `EntraAuthConfig` accepted by `<EntraAuthProvider>`.
 */
export function toEntraAuthConfig(config: MsalConfig): EntraAuthConfig {
  return {
    tenantId: config.tenantId,
    clientId: config.clientId,
    apiScope: config.apiScope,
    redirectUri: config.redirectUri,
  };
}

/**
 * Hook returning the {@link TokenClient} that `createAuthedFetch` depends on,
 * implemented over the package's {@link useAuth} surface.
 *
 * - `getToken()`     → `getAccessToken()` (silent; defaults to the provider's
 *   `apiScope`, so callers do not re-specify the scope — Req 3.5/3.6).
 * - `refreshToken()` → `getAccessToken({ forceRefresh: true })`, bypassing
 *   MSAL's in-memory cache to force a fresh token (Req 4.1).
 * - `signIn()`       → `login()` (interactive redirect sign-in).
 *
 * The returned object identity is **stable for the component's lifetime**: the
 * latest `auth` surface is held in a ref and the methods read `authRef.current`
 * at call time, so the `TokenClient` reference does NOT change when `useAuth()`
 * returns a fresh object on re-render. This is essential because consumers put
 * the derived `fetchFn` in `useEffect`/`useMemo` dependency arrays
 * (`fetchRunsCatalog`, `useRunLoader`, `RepoDetail`); a churning identity would
 * re-run those effects on every render, aborting and re-issuing the in-flight
 * data request in a tight loop (perpetual "loading").
 */
export function useTokenClient(): TokenClient {
  const auth = useAuth();

  // Hold the latest auth surface in a ref so the memoized client can read it at
  // call time without depending on its (possibly per-render) identity.
  const authRef = useRef(auth);
  authRef.current = auth;

  return useMemo<TokenClient>(
    () => ({
      getToken: () => authRef.current.getAccessToken(),
      refreshToken: () => authRef.current.getAccessToken({ forceRefresh: true }),
      signIn: () => authRef.current.login(),
    }),
    // Empty deps: a single stable identity for the component's lifetime.
    [],
  );
}

/**
 * Hook returning whether the current user holds the dashboard viewer role
 * ({@link VIEWER_ROLE}). Thin wrapper over the package's `useHasRole` so the
 * role name lives in exactly one place (Req 13.5).
 */
export function useViewerRole(): boolean {
  return useHasRole(VIEWER_ROLE);
}

/**
 * Hook returning a sign-out callback over `useAuth().logout`. The returned
 * function identity is memoized against the `auth` surface.
 */
export function useSignOut(): () => Promise<void> {
  const auth = useAuth();
  return useMemo(() => () => auth.logout(), [auth]);
}

/**
 * Re-exported package components for the app wrapper (task 3.12) to consume via
 * this adapter module rather than importing the package directly, preserving
 * the single-import-site invariant (Req 13.1).
 */
export { EntraAuthProvider, RequireAuth };
export type { EntraAuthConfig };
